/**
 * 🧰 [Phase 17] Comparaison matériel par norme.
 *
 * Tableau : articles × unités. Cellules colorées selon écart vs norme.
 * Mode owner uniquement pour définir les normes — sur cette page on
 * affiche en lecture (la définition se fait via la fiche article, futur ajout).
 */
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/lib/AuthContext';
import { Card, CardContent } from '@/components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmt(n) {
  if (n === null || n === undefined) return '—';
  return Math.abs(n - Math.round(n)) < 0.01 ? String(Math.round(n)) : n.toFixed(1);
}

export default function ComparaisonMateriel() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [ecartsOnly, setEcartsOnly] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/unites/comparaison-materiel`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || 'Erreur de chargement'));
  }, []);

  const articlesAffiches = useMemo(() => {
    if (!data) return [];
    if (!ecartsOnly) return data.articles;
    return data.articles.filter(a =>
      Object.values(a.lignes_par_unite).some(l => l.ecart !== null && l.ecart < 0)
    );
  }, [data, ecartsOnly]);

  if (error) return <div className="p-6 text-red-600" data-testid="error">🚫 {error}</div>;
  if (!data) return <div className="p-6 text-slate-500" data-testid="loading">Chargement...</div>;

  return (
    <div className="p-4 md:p-6 max-w-full" data-testid="page-comparaison-materiel">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
        <h1 className="text-2xl font-bold text-slate-900">🧰 Comparaison matériel par norme</h1>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={ecartsOnly}
            onChange={e => setEcartsOnly(e.target.checked)}
            data-testid="toggle-ecarts-only"
          />
          Afficher uniquement les écarts (manquants)
        </label>
      </div>

      {articlesAffiches.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-slate-500">
          {ecartsOnly ? 'Tous les articles sont conformes aux normes définies.' : 'Aucun article avec données.'}
        </CardContent></Card>
      ) : (
        <div className="overflow-x-auto border rounded-lg bg-white shadow-sm">
          <table className="min-w-full text-sm" data-testid="table-comparaison">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-semibold sticky left-0 bg-slate-100 z-10">Article</th>
                {data.unites.map(u => (
                  <th key={u.id} className="px-2 py-2 text-center font-semibold whitespace-nowrap" data-testid={`th-unite-${u.id}`}>
                    <div className="text-xs">{u.numero_unite ? `#${u.numero_unite}` : ''}</div>
                    <div className="text-[10px] text-slate-500 font-normal">{u.nom}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {articlesAffiches.map(a => (
                <tr key={a.id} className="border-t hover:bg-blue-50/40" data-testid={`row-${a.id}`}>
                  <td className="px-3 py-2 font-medium sticky left-0 bg-white">
                    {a.nom}
                    {a.numero_inventaire && <span className="text-xs text-slate-400 ml-1">#{a.numero_inventaire}</span>}
                  </td>
                  {data.unites.map(u => {
                    const cell = a.lignes_par_unite[u.id] || { actuelle: 0, norme: null, ecart: null };
                    let cls = 'text-slate-700';
                    let label = fmt(cell.actuelle);
                    if (cell.norme !== null) {
                      if (cell.ecart < 0) cls = 'bg-red-50 text-red-700 font-semibold';
                      else if (cell.ecart === 0) cls = 'bg-emerald-50 text-emerald-700';
                      else cls = 'bg-amber-50 text-amber-700';
                      label = `${fmt(cell.actuelle)} / ${fmt(cell.norme)}`;
                    }
                    return (
                      <td key={u.id} className={`px-2 py-2 text-center ${cls}`} data-testid={`cell-${a.id}-${u.id}`}>
                        <div>{label}</div>
                        {cell.ecart !== null && cell.ecart < 0 && (
                          <div className="text-[10px] font-bold">Manque {Math.abs(cell.ecart)}</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-slate-500 mt-3">
        Légende : <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">Conforme</span>
        {' '}<span className="bg-red-50 text-red-700 px-1.5 py-0.5 rounded">Manque</span>
        {' '}<span className="bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">Surplus</span>
        {' '}— format <code>actuelle / norme</code>. Cellules grises = aucune norme définie.
      </p>
    </div>
  );
}
